import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Trophy, Medal, Award, Crown, Star, BarChart3, Car, Clock, X } from "lucide-react";

const TIERS = {
  1: {
    rank: "1° POSTO", tag: "TOP DRIVER DEL MESE", icon: Trophy, ctaIcon: Crown,
    title: "Complimenti, Campione!",
    msg: "Sei il miglior driver del mese di Striano NCC. Il tuo impegno, la tua professionalità e la costanza che dimostri ogni giorno fanno la differenza. Continua così: sei un esempio per tutta la squadra!",
    cta: "Continua a dare il massimo!",
    metal: "linear-gradient(135deg,#FFF3C4 0%,#F1C75B 28%,#B8862B 55%,#F6DB8A 78%,#9C6B1A 100%)",
    glow: "rgba(241,199,91,0.35)", border: "border-amber-400/50", accent: "text-amber-300", bg: "from-[#1A1305] via-[#0B0906] to-[#050505]",
  },
  2: {
    rank: "2° POSTO", tag: "ELITE DRIVER DEL MESE", icon: Medal, ctaIcon: Star,
    title: "Ottimo lavoro!",
    msg: "Sei tra i migliori driver del mese di Striano NCC. I tuoi risultati dimostrano serietà, impegno e professionalità. Continua così: il primo posto è alla tua portata!",
    cta: "Ogni servizio è un passo avanti!",
    metal: "linear-gradient(135deg,#FFFFFF 0%,#D7DEE8 30%,#8E9BB0 55%,#E9EEF5 78%,#7C8AA3 100%)",
    glow: "rgba(147,197,253,0.30)", border: "border-sky-300/40", accent: "text-sky-200", bg: "from-[#0B1730] via-[#070D1C] to-[#04070F]",
  },
  3: {
    rank: "3° POSTO", tag: "EXCELLENCE DRIVER DEL MESE", icon: Award, ctaIcon: BarChart3,
    title: "Complimenti per il risultato!",
    msg: "Hai conquistato il podio dei migliori driver del mese. Il tuo impegno viene riconosciuto e apprezzato. Continua a dare il meglio in ogni servizio!",
    cta: "La costanza porta sempre lontano!",
    metal: "linear-gradient(135deg,#FCE1C8 0%,#D9925A 30%,#8F4E24 58%,#E8A877 80%,#7A3E17 100%)",
    glow: "rgba(217,146,90,0.32)", border: "border-orange-400/40", accent: "text-orange-300", bg: "from-[#1C0E06] via-[#0C0705] to-[#050505]",
  },
};
const MONTHS = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const metalText = (g) => ({ backgroundImage: g, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" });

function Stat({ icon: I, label, value, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-3">
      <I size={20} className="text-slate-300" />
      <div><div className="text-[11px] text-slate-400">{label}</div><div className="text-lg font-bold text-white" data-testid={testid}>{value}</div></div>
    </div>
  );
}

function Badge({ t, r, onOpen }) {
  const I = t.icon;
  return (
    <button type="button" onClick={onOpen} data-testid="recognition-badge"
      className={`w-full rounded-2xl border ${t.border} bg-gradient-to-r ${t.bg} px-4 py-3 flex items-center gap-3 text-left transition-transform active:scale-[0.99]`}>
      <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: t.metal }}><I size={20} className="text-black/70" /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-extrabold" style={metalText(t.metal)}>{t.rank} · {t.tag.replace(" DEL MESE", "")}</span>
        <span className="block text-xs text-slate-400">{MONTHS[r.month]} {r.year} · tocca per rivedere</span>
      </span>
    </button>
  );
}

export function DriverRecognition() {
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/driver/me/recognition").then(res => { setR(res.data); setOpen(res.data.place && !res.data.seen); }).catch(() => setR(null));
  }, []);
  if (!r || !TIERS[r.place]) return null;
  const t = TIERS[r.place];
  if (!open) return <Badge t={t} r={r} onOpen={() => setOpen(true)} />;
  const close = () => { setOpen(false); if (!r.seen) { api.post("/driver/me/recognition/seen").catch(() => {}); setR({ ...r, seen: true }); } };
  const I = t.icon, C = t.ctaIcon;
  return (
    <section data-testid={`recognition-card-${r.place}`}
      className={`relative overflow-hidden rounded-3xl border ${t.border} bg-gradient-to-b ${t.bg} px-5 pt-6 pb-5 sm:px-8 animate-[recIn_.6s_ease-out]`}
      style={{ boxShadow: `0 0 60px -20px ${t.glow}` }}>
      <style>{`@keyframes recIn{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}
        @keyframes recShine{0%{transform:translateX(-120%) rotate(20deg)}100%{transform:translateX(220%) rotate(20deg)}}
        @keyframes recFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}`}</style>
      <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full blur-3xl" style={{ background: t.glow }} />
      <button type="button" onClick={close} aria-label="Chiudi" data-testid="recognition-close"
        className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5"><X size={18} /></button>
      <div className="relative flex flex-col items-center text-center">
        <div className="relative w-24 h-24 rounded-full flex items-center justify-center overflow-hidden animate-[recFloat_4s_ease-in-out_infinite]"
          style={{ background: t.metal, boxShadow: `0 0 34px ${t.glow}, inset 0 2px 6px rgba(255,255,255,.6)` }}>
          <I size={46} strokeWidth={1.6} className="text-black/70" />
          <span className="absolute inset-y-0 w-8 bg-white/40 blur-sm animate-[recShine_3.5s_ease-in-out_infinite]" />
        </div>
        <div className="mt-3 px-5 py-1 rounded-md text-sm font-black tracking-wider text-black/80" style={{ background: t.metal }} data-testid="recognition-rank">{t.rank}</div>
        <div className="mt-2 text-[11px] tracking-[0.28em] text-slate-300">{t.tag}</div>
        <div className="text-[10px] tracking-[0.3em] text-slate-500">STRIANO NCC · {MONTHS[r.month].toUpperCase()} {r.year}</div>
        <h3 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold" style={metalText(t.metal)} data-testid="recognition-title">{t.title}</h3>
        <p className="mt-3 max-w-xl text-sm sm:text-base text-slate-200 leading-relaxed">{t.msg}</p>
        <div className={`mt-4 w-full max-w-md rounded-xl border ${t.border} px-4 py-3 flex items-center justify-center gap-2 font-semibold ${t.accent}`} data-testid="recognition-cta">
          <C size={18} />{t.cta}
        </div>
      </div>
      <div className="relative mt-5">
        <div className="flex items-center gap-2 text-sm text-slate-300 mb-2"><BarChart3 size={16} className={t.accent} />I tuoi risultati di questo mese</div>
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={Car} label="Servizi" value={r.stats.services} testid="recognition-services" />
          <Stat icon={Clock} label="Ore di servizio" value={`${Math.round(r.stats.hours)}h`} testid="recognition-hours" />
        </div>
      </div>
      <div className="relative mt-3 text-[11px] text-slate-500 text-center">Classifica del mese in corso, aggiornata in tempo reale.</div>
    </section>
  );
}
